import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Text } from "@react-three/drei";
import { useStore } from "../store";
import { POS } from "./layout";

const WALK_LOOPS = [
  [[-222, -242], [178, -242], [178, 202], [-222, 202]],
  [[-214, -234], [170, -234], [170, 194], [-214, 194]],
  [[-398, -252], [-378, -252], [-378, 452], [-398, 452]],
  [[-120, -180], [90, -180], [90, 150], [-120, 150]],
];
const SHIRT = ["#c0392b", "#2e86c1", "#27ae60", "#8e44ad", "#e67e22", "#16a085", "#2c3e50", "#d4ac0d", "#c0446b", "#455a64"];
const PANTS = ["#2c3e50", "#34495e", "#4a3b2a", "#1c2833", "#5d4037", "#37474f"];
const SKIN = ["#e8c39e", "#c68b5f", "#8d5a3c", "#f0d0b0", "#a56a45", "#5e3d28"];

function rnd(s) {
  s.s = (s.s * 1664525 + 1013904223) % 4294967296;
  return s.s / 4294967296;
}

// Blocky 'Roblox-style' NPCs built from box primitives (torso, cube head, 2 arms, 2 legs).
export default function Pedestrians() {
  const { gl } = useThree();
  const lowPerf = useMemo(() => window.innerWidth < 820 || (gl.capabilities && gl.capabilities.maxTextureSize < 4096), [gl]);
  const refs = {
    torso: useRef(),
    head: useRef(),
    armL: useRef(),
    armR: useRef(),
    legL: useRef(),
    legR: useRef(),
  };

  const curves = useMemo(
    () => WALK_LOOPS.map((pts) => new THREE.CatmullRomCurve3(pts.map(([x, z]) => new THREE.Vector3(x, 0, z)), true, "catmullrom", 0.05)),
    []
  );

  const { people, total, walkerCount, labels } = useMemo(() => {
    const seed = { s: 20260911 };
    const walkers = lowPerf ? 400 : 1300;
    const standing = lowPerf ? 80 : 200;
    const people = [];
    for (let i = 0; i < walkers; i++) {
      people.push({
        walk: true,
        loop: Math.floor(rnd(seed) * curves.length),
        u: rnd(seed),
        speed: 0.005 + rnd(seed) * 0.007,
        h: 0.92 + rnd(seed) * 0.16,
        phase: rnd(seed) * Math.PI * 2,
        shirt: SHIRT[Math.floor(rnd(seed) * SHIRT.length)],
        pants: PANTS[Math.floor(rnd(seed) * PANTS.length)],
        skin: SKIN[Math.floor(rnd(seed) * SKIN.length)],
      });
    }
    const pools = [POS.northPool, POS.southPool];
    for (let i = 0; i < standing; i++) {
      let cx, cz;
      if (i % 5 < 3) {
        const pool = pools[i % 2];
        const a = rnd(seed) * Math.PI * 2;
        const r = 46 + rnd(seed) * 16;
        cx = pool[0] + Math.cos(a) * r;
        cz = pool[2] + Math.sin(a) * r;
      } else {
        cx = -70 + rnd(seed) * 130;
        cz = -60 + rnd(seed) * 140;
      }
      people.push({
        walk: false, x: cx, z: cz, rot: rnd(seed) * Math.PI * 2,
        h: 0.92 + rnd(seed) * 0.16, phase: rnd(seed) * Math.PI * 2,
        shirt: SHIRT[Math.floor(rnd(seed) * SHIRT.length)],
        pants: PANTS[Math.floor(rnd(seed) * PANTS.length)],
        skin: SKIN[Math.floor(rnd(seed) * SKIN.length)],
      });
    }
    // gold TAB labels above a few standing plaza NPCs
    const labels = [];
    for (let i = walkers; i < walkers + 6 && i < people.length; i++) {
      const pe = people[i];
      labels.push({ x: pe.x, y: 2.9 * pe.h, z: pe.z, text: i % 2 ? "Visitor" : "Commuter" });
    }
    return { people, total: people.length, walkerCount: walkers, labels };
  }, [curves, lowPerf]);

  const d = useMemo(() => new THREE.Object3D(), []);
  const q = useMemo(() => new THREE.Quaternion(), []);
  const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const p = useMemo(() => new THREE.Vector3(), []);
  const tan = useMemo(() => new THREE.Vector3(), []);

  // write all six limb matrices for one NPC
  const writeFigure = (i, x, z, yaw, h, sw) => {
    q.setFromAxisAngle(up, yaw);
    const fx = Math.sin(yaw), fz = Math.cos(yaw); // forward
    const rx = Math.cos(yaw), rz = -Math.sin(yaw); // right
    const put = (ref, lx, ly, lz, sx, sy, sz) => {
      d.position.set(x + rx * lx + fx * lz, ly * h, z + rz * lx + fz * lz);
      d.quaternion.copy(q);
      d.scale.set(sx * h, sy * h, sz * h);
      d.updateMatrix();
      ref.current.setMatrixAt(i, d.matrix);
    };
    put(refs.legL, -0.22, 0.42, sw, 0.3, 0.85, 0.3);
    put(refs.legR, 0.22, 0.42, -sw, 0.3, 0.85, 0.3);
    put(refs.torso, 0, 1.25, 0, 0.85, 0.9, 0.46);
    put(refs.head, 0, 2.0, 0, 0.62, 0.62, 0.62);
    put(refs.armL, -0.62, 1.3, -sw, 0.26, 0.85, 0.26);
    put(refs.armR, 0.62, 1.3, sw, 0.26, 0.85, 0.26);
  };

  useLayoutEffect(() => {
    const col = new THREE.Color();
    const paint = (ref, getColor) => {
      people.forEach((pe, i) => {
        col.set(getColor(pe));
        ref.current?.setColorAt(i, col);
      });
      if (ref.current?.instanceColor) ref.current.instanceColor.needsUpdate = true;
    };
    paint(refs.torso, (pe) => pe.shirt);
    paint(refs.armL, (pe) => pe.shirt);
    paint(refs.armR, (pe) => pe.shirt);
    paint(refs.legL, (pe) => pe.pants);
    paint(refs.legR, (pe) => pe.pants);
    paint(refs.head, (pe) => pe.skin);
    people.forEach((pe, i) => {
      let x = pe.x, z = pe.z, yaw = pe.rot || 0;
      if (pe.walk) {
        const c = curves[pe.loop];
        c.getPointAt(pe.u, p);
        c.getTangentAt(pe.u, tan);
        x = p.x; z = p.z; yaw = Math.atan2(tan.x, tan.z);
      }
      writeFigure(i, x, z, yaw, pe.h, 0);
    });
    Object.values(refs).forEach((r) => r.current && (r.current.instanceMatrix.needsUpdate = true));
  }, [people]);

  useFrame((state, delta) => {
    if (!refs.torso.current) return;
    const t = state.clock.elapsedTime;
    const scale = THREE.MathUtils.clamp(useStore.getState().qualityScale, 0.25, 1);
    const active = Math.floor(walkerCount * scale);
    people.forEach((pe, i) => {
      if (pe.walk) {
        if (i >= active) return; // LOD freeze
        pe.u = (pe.u + pe.speed * delta) % 1;
        const c = curves[pe.loop];
        c.getPointAt(pe.u, p);
        c.getTangentAt(pe.u, tan);
        const sw = Math.sin(t * 3.5 + pe.phase) * 0.28 * pe.h; // slow, somber stride
        writeFigure(i, p.x, p.z, Math.atan2(tan.x, tan.z), pe.h, sw);
      } else {
        const sw = Math.sin(t * 0.7 + pe.phase) * 0.05 * pe.h; // idle
        writeFigure(i, pe.x, pe.z, pe.rot, pe.h, sw);
      }
    });
    Object.values(refs).forEach((r) => (r.current.instanceMatrix.needsUpdate = true));
  });

  const parts = [
    ["torso", [0.85, 0.9, 0.46]],
    ["head", [0.62, 0.62, 0.62]],
    ["armL", [0.26, 0.85, 0.26]],
    ["armR", [0.26, 0.85, 0.26]],
    ["legL", [0.3, 0.85, 0.3]],
    ["legR", [0.3, 0.85, 0.3]],
  ];

  return (
    <group>
      {parts.map(([name]) => (
        <instancedMesh key={name} ref={refs[name]} args={[null, null, total]} castShadow frustumCulled={false}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial roughness={0.85} />
        </instancedMesh>
      ))}
      {labels.map((l, i) => (
        <Text key={i} position={[l.x, l.y, l.z]} fontSize={1.1} color="#D4AF37" anchorX="center" anchorY="middle" outlineWidth={0.04} outlineColor="#1a1205">
          {l.text}
        </Text>
      ))}
    </group>
  );
}
